import { useContext, useEffect, useMemo, useState } from "react";
import { UserContext } from "../../UserContext";

import { getReservations } from "../../api/reservationsService";
import { fetchInstructorReservationsForRange } from "../../utils/instructorReservations";
import { isSubstituteInstructorType } from "../../constants/instructors";
import { getInstructors } from "../../api/instructorsService";
import { listenCalendarRefresh } from "../Utils/calendarBus";

const SUBSTITUTE_HOME_REFRESH_MS = 35000;

function toFloatingDate(val) {
   if (!val) return null;

   if (val instanceof Date && !Number.isNaN(val.getTime())) {
      return new Date(
         val.getFullYear(),
         val.getMonth(),
         val.getDate(),
         val.getHours(),
         val.getMinutes(),
         val.getSeconds(),
         val.getMilliseconds(),
      );
   }

   if (typeof val === "string") {
      const matchDateTime = val.match(
         /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?$/,
      );
      if (matchDateTime) {
         const [, year, month, day, hour, minute, seconds] = matchDateTime;
         return new Date(
            Number(year),
            Number(month) - 1,
            Number(day),
            Number(hour),
            Number(minute),
            seconds ? Number(seconds) : 0,
            0,
         );
      }

      const matchDateOnly = val.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (matchDateOnly) {
         const [, year, month, day] = matchDateOnly;
         return new Date(
            Number(year),
            Number(month) - 1,
            Number(day),
            0,
            0,
            0,
            0,
         );
      }
   }

   const parsed = new Date(val);
   if (!Number.isNaN(parsed.getTime())) {
      return new Date(
         parsed.getFullYear(),
         parsed.getMonth(),
         parsed.getDate(),
         parsed.getHours(),
         parsed.getMinutes(),
         parsed.getSeconds(),
         parsed.getMilliseconds(),
      );
   }

   return null;
}

function formatStudentLabel(eventItem) {
   const student = eventItem?.student;
   if (student) {
      const first = String(student.firstName || "").trim();
      const last = String(student.lastName || "").trim();
      const fullName = `${first} ${last}`.trim();
      if (fullName) return fullName;
   }

   const studentsCount = Number(eventItem?.studentsCount || 0);
   if (studentsCount > 1) return `${studentsCount} studenți`;
   return "Student neidentificat";
}

function formatMetaLine(eventItem) {
   const parts = [eventItem?.sector, eventItem?.gearbox]
      .map((entry) => String(entry || "").trim())
      .filter(Boolean);
   return parts.length > 0 ? parts.join(" · ") : "Detalii indisponibile";
}

function normalizeEventStudents(item) {
   if (Array.isArray(item.students) && item.students.length) {
      return {
         list: item.students,
         one: item.students.length === 1 ? item.students[0] : null,
      };
   }
   if (Array.isArray(item.participants) && item.participants.length) {
      return {
         list: item.participants,
         one: item.participants.length === 1 ? item.participants[0] : null,
      };
   }
   if (Array.isArray(item.users) && item.users.length) {
      return {
         list: item.users,
         one: item.users.length === 1 ? item.users[0] : null,
      };
   }
   const one = item.student || item.user || null;
   return { list: one ? [one] : [], one };
}

function toTodayEvent(item) {
   const start = toFloatingDate(item.startTime);
   if (!start) return null;

   const end = item.endTime
      ? toFloatingDate(item.endTime)
      : new Date(start.getTime() + 90 * 60 * 1000);

   const safeEnd =
      end && !Number.isNaN(end.getTime())
         ? end
         : new Date(start.getTime() + 90 * 60 * 1000);

   const { list: studentsArr, one: studentObj } = normalizeEventStudents(item);

   const studentPhone =
      studentObj?.phone ||
      studentObj?.tel ||
      studentObj?.phoneNumber ||
      studentObj?.phone_number ||
      null;

   return {
      id: item.id,
      start,
      end: safeEnd,
      gearbox: item.gearbox,
      sector: item.sector,
      isConfirmed: !!item.isConfirmed,
      isFavorite: Boolean(item?.isFavorite ?? item?.is_favorite),
      isImportant: Boolean(item?.isImportant ?? item?.is_important),

      student: studentObj || null,
      students: studentsArr,
      studentsCount:
         item.studentsCount ?? item.totalStudents ?? studentsArr.length,

      instructor: item.instructor,
      phone: studentPhone,
   };
}

function reservationStartsInRange(row, rangeStart, rangeEnd) {
   const start = toFloatingDate(row?.startTime);
   if (!start) return false;
   const ts = start.getTime();
   const fromTs =
      rangeStart instanceof Date && !Number.isNaN(rangeStart.getTime())
         ? rangeStart.getTime()
         : Number.NEGATIVE_INFINITY;
   const toTs =
      rangeEnd instanceof Date && !Number.isNaN(rangeEnd.getTime())
         ? rangeEnd.getTime()
         : Number.POSITIVE_INFINITY;
   return ts >= fromTs && ts <= toTs;
}

function TodayInfo() {
   const { user } = useContext(UserContext);

   // prefer numele de pe instructor pentru rolul INSTRUCTOR
   const [myInstructor, setMyInstructor] = useState(null);
   const [substitutionRefreshTick, setSubstitutionRefreshTick] = useState(0);

   useEffect(() => {
      return listenCalendarRefresh((payload) => {
         if (
            String(payload?.type || "")
               .trim()
               .toLowerCase() !== "substitutions-changed"
         ) {
            return;
         }
         setSubstitutionRefreshTick((tick) => tick + 1);
      });
   }, []);

   useEffect(() => {
      if (!isSubstituteInstructorType(myInstructor?.instructorType)) return undefined;
      if (typeof window === "undefined") return undefined;

      const intervalId = window.setInterval(() => {
         if (document.hidden) return;
         setSubstitutionRefreshTick((tick) => tick + 1);
      }, SUBSTITUTE_HOME_REFRESH_MS);

      return () => {
         window.clearInterval(intervalId);
      };
   }, [myInstructor?.instructorType]);

   const [events, setEvents] = useState([]);

   // refresh “acum” la ~30s ca să comute clasa --active
   const [now, setNow] = useState(new Date());
   useEffect(() => {
      const id = setInterval(() => setNow(new Date()), 30 * 1000);
      return () => clearInterval(id);
   }, []);
   const todayDayKey = useMemo(() => {
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, "0");
      const d = String(now.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
   }, [now]);
   const todayRangeIso = useMemo(() => {
      const [yy, mm, dd] = todayDayKey.split("-").map(Number);
      const start = new Date(yy, Number(mm) - 1, dd, 0, 0, 0, 0);
      const end = new Date(yy, Number(mm) - 1, dd, 23, 59, 59, 999);
      return {
         start: start.toISOString(),
         end: end.toISOString(),
      };
   }, [todayDayKey]);

   // 1) găsește instructorul curent după userId
   useEffect(() => {
      let cancelled = false;
      (async () => {
         if (!user || user.role !== "INSTRUCTOR") {
            setMyInstructor(null);
            return;
         }
         try {
            const all = await getInstructors();
            const mine = all.find((i) => String(i.userId) === String(user.id));
            if (!cancelled) setMyInstructor(mine || null);
         } catch (e) {
            if (!cancelled) setMyInstructor(null);
         }
      })();
      return () => {
         cancelled = true;
      };
   }, [user]);

   // 2) ia rezervările potrivite rolului
   useEffect(() => {
      let cancelled = false;
      (async () => {
         try {
            let raw = [];
            if (user?.role === "INSTRUCTOR") {
               if (!myInstructor?.id) return;
               const rangeStart = new Date(todayRangeIso.start);
               const rangeEnd = new Date(todayRangeIso.end);
               raw = await fetchInstructorReservationsForRange(
                  myInstructor,
                  user,
                  {
                     startDateFrom: todayRangeIso.start,
                     startDateTo: todayRangeIso.end,
                  },
                  {
                     pageSize: 200,
                     maxItems: 2000,
                     preferSinglePage: true,
                     singlePageLimit: 2000,
                     singlePageAssumeCompleteIfUnderLimit: true,
                  },
               );
               raw = raw.filter((row) =>
                  reservationStartsInRange(row, rangeStart, rangeEnd),
               );
            } else {
               raw = await getReservations();
            }

            const mapped = raw.map(toTodayEvent).filter(Boolean);
            if (cancelled) return;

            setEvents(mapped);
         } catch (e) {}
      })();
      return () => {
         cancelled = true;
      };
   }, [user, myInstructor, todayRangeIso.start, todayRangeIso.end, substitutionRefreshTick]);

   // === TODAY VIEW (grupare pe azi) ===
   const isSameDay = (a, b) =>
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();

   const today = now;
   const todaysEvents = useMemo(
      () =>
         events
            .filter((e) => isSameDay(e.start, today))
            .sort((a, b) => a.start - b.start),
      [events, today],
   );

   const current = useMemo(
      () => todaysEvents.filter((e) => e.start <= now && now < e.end),
      [todaysEvents, now],
   );
   const upcoming = useMemo(
      () => todaysEvents.filter((e) => e.start > now),
      [todaysEvents, now],
   );
   const past = useMemo(
      () => todaysEvents.filter((e) => e.end <= now),
      [todaysEvents, now],
   );

   const fmtHour = (d) =>
      new Date(d).toLocaleTimeString("ro-RO", {
         hour: "2-digit",
         minute: "2-digit",
      });

   const fmtDateTitle = (d) =>
      `Azi – ${d
         .toLocaleDateString("ro-RO", { day: "numeric", month: "short" })
         .replaceAll(".", "")}`;

   const sections = [
      {
         key: "current",
         title: "Acum",
         items: current,
         bucketLabel: "În desfășurare",
      },
      {
         key: "upcoming",
         title: "Urmează",
         items: upcoming,
         bucketLabel: "Programată",
      },
      {
         key: "past",
         title: "Trecute (astăzi)",
         items: past,
         bucketLabel: "Finalizată",
      },
   ].filter((section) => section.items.length > 0);

   return (
      <section className="today">
         <div className="today__header">
            <h2 className="today__title">{fmtDateTitle(today)}</h2>
         </div>

         <div className="today__grid-wrapper">
            <div className="today__grid">
               {sections.map((section) => (
                  <section
                     key={section.key}
                     className={`today__section today__section--${section.key}`}
                  >
                     <div className="today__section-head">
                        <h3 className="today__section-title">
                           {section.title}
                        </h3>
                        <span className="today__section-count">
                           {section.items.length}
                        </span>
                     </div>

                     <div className="today__items">
                        {section.items.map((ev) => {
                           const isActive = now >= ev.start && now < ev.end;
                           return (
                              <article
                                 key={String(
                                    ev.id ??
                                       `${ev.start ? ev.start.getTime() : "event"}-${section.key}`,
                                 )}
                                 className={[
                                    "today__card",
                                    `today__card--${section.key}`,
                                    section.key === "past"
                                       ? "today__card--past"
                                       : "",
                                    isActive ? "today__card--active" : "",
                                 ]
                                    .filter(Boolean)
                                    .join(" ")}
                              >
                                 <div className="today__card-main">
                                    <p className="today__card-time">
                                       {fmtHour(ev.start)} – {fmtHour(ev.end)}
                                    </p>
                                    <p className="today__card-name">
                                       {formatStudentLabel(ev)}
                                    </p>
                                    {ev.phone ? (
                                       <p className="today__card-phone">
                                          {ev.phone}
                                       </p>
                                    ) : null}
                                    <p className="today__card-meta">
                                       {formatMetaLine(ev)}
                                       {ev.isFavorite ? (
                                          <span className="today__card-flag">
                                             {" "}
                                             ⁂
                                          </span>
                                       ) : null}
                                       {ev.isImportant ? (
                                          <span className="today__card-flag">
                                             {" "}
                                             ‼
                                          </span>
                                       ) : null}
                                    </p>
                                 </div>
                              </article>
                           );
                        })}
                     </div>
                  </section>
               ))}

               {todaysEvents.length === 0 && (
                  <p className="today__empty">Nu ai programări astăzi.</p>
               )}
            </div>
         </div>
      </section>
   );
}

export default TodayInfo;
